import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { usePropertyContext } from '@/contexts/PropertyContext';
import { getNavSections } from '@/config/navigation';
import { UserRole } from '@pms/shared';
import { cn } from '@/lib/utils';

/**
 * Property hub — the HotelKey-style tile launcher shown when entering a
 * property. Groups (Operations / Revenue Management / RetailKey /
 * Administration) and their tiles are driven by the same role-based nav config
 * that powers the sidebar, so the two never drift apart.
 */
export function PropertyHubPage() {
  const { user } = useAuth();
  const { selectedProperty } = usePropertyContext();
  const role = user?.role ?? UserRole.OWNER;

  // Skip the untitled standalone section (Home/Dashboard) — the hub IS Home.
  const groups = getNavSections(role).filter((s) => s.title.length > 0);

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
      <header className="mb-8">
        <p className="text-sm font-medium text-slate-500">Property Console</p>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900">
          {selectedProperty?.name ?? 'Select a property'}
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Choose a module to get started.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {groups.map((group) => (
          <section
            key={group.title}
            className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
          >
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
              {group.title}
            </h2>
            <ul className="divide-y divide-slate-100">
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={cn(
                        'group flex items-center gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-slate-50',
                        item.readOnly && 'pointer-events-none opacity-60',
                      )}
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600 group-hover:bg-blue-50 group-hover:text-blue-600">
                        <Icon className="h-5 w-5" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-slate-800">
                          {item.label}
                        </span>
                        {item.description && (
                          <span className="block truncate text-xs text-slate-500">
                            {item.description}
                          </span>
                        )}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
